import React from "react";
import { Code, Modal, SimpleGrid, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { AuditEntry } from "@models/access";
import { auditActor, auditTarget } from "./audit";
import { spacePx } from "@xplatform/design-system";

const pretty = (value: unknown): string =>
  value === undefined || value === null ? "-" : JSON.stringify(value, null, 2);

/** One audit entry: who did what to which target, and the before / after state. */
const AuditDetailModal: React.FC<{ entry: AuditEntry | null; onClose: () => void }> = ({ entry, onClose }) => {
  const { t } = useTranslation();
  return (
    <Modal
      opened={entry !== null}
      onClose={onClose}
      size="xl"
      title={entry ? `${entry.action ?? "-"} · ${auditTarget(entry).name ?? auditTarget(entry).id}` : ""}
      portalProps={{ target: "#admin-portal-root" }}
    >
      {entry && (
        <Stack gap="md">
          <Text size="sm">
            {t("admin.access.audit.detail.by", { actor: auditActor(entry), ts: entry.at ?? "-" })}
          </Text>
          {entry.error && (
            <Text size="sm" c="red">{[entry.error.code, entry.error.message].filter(Boolean).join(": ")}</Text>
          )}
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap={spacePx[1]}>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase">{t("admin.access.audit.detail.before")}</Text>
              <Code block>{pretty(entry.before)}</Code>
            </Stack>
            <Stack gap={spacePx[1]}>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase">{t("admin.access.audit.detail.after")}</Text>
              <Code block>{pretty(entry.after)}</Code>
            </Stack>
          </SimpleGrid>
          <Text size="xs" c="dimmed" ff="monospace">{entry.requestId}</Text>
        </Stack>
      )}
    </Modal>
  );
};

export default AuditDetailModal;
